import { Routes, Route } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { AppProvider } from './contexts/AppContext'
import { IntegrationsProvider } from './contexts/IntegrationsContext'
import { FocusProvider } from './contexts/FocusContext'
import Header from './components/Header'
import Footer from './components/Footer'
import ProtectedRoute from './components/ProtectedRoute'
import Home from './pages/Home'
import HowItWorks from './pages/HowItWorks'
import Features from './pages/Features'
import Pricing from './pages/Pricing'
import About from './pages/About'
import FAQ from './pages/FAQ'
import Blog from './pages/Blog'
import Signup from './pages/Signup'
import Login from './pages/Login'

// App screens
import AppLayout from './components/app/AppLayout'
import AppToday from './pages/app/AppToday'
import AppCalendar from './pages/app/AppCalendar'
import AppInbox from './pages/app/AppInbox'
import AppProfile from './pages/app/AppProfile'
import AppConnections from './pages/app/AppConnections'
import AppOAuthCallback from './pages/app/AppOAuthCallback'
import SlackOAuthCallback from './pages/app/SlackOAuthCallback'
import NotionOAuthCallback from './pages/app/NotionOAuthCallback'
import AppFocusMode from './pages/app/AppFocusMode'
import AppVoiceCapture from './pages/app/AppVoiceCapture'
import AppAnalytics from './pages/app/AppAnalytics'
import AppTaskDetail from './pages/app/AppTaskDetail'
import AppEventDetail from './pages/app/AppEventDetail'
import AppScheduleNegotiation from './pages/app/AppScheduleNegotiation'
import AppMorningBriefing from './pages/app/AppMorningBriefing'
import AppEveningDebrief from './pages/app/AppEveningDebrief'
import AppPriorityBoard from './pages/app/AppPriorityBoard'
import AppHabits from './pages/app/AppHabits'
import AppGoals from './pages/app/AppGoals'
import Changelog from './pages/app/Changelog'
import AppHelpCenter from './pages/app/AppHelpCenter'
import AppContactSupport from './pages/app/AppContactSupport'
import AppFeatureRequests from './pages/app/AppFeatureRequests'
import AppTermsOfService from './pages/app/AppTermsOfService'
import AppPrivacyPolicy from './pages/app/AppPrivacyPolicy'
import AppPrivacySettings from './pages/app/AppPrivacySettings'
import AppSubscriptionBilling from './pages/app/AppSubscriptionBilling'
import AppAutoSchedulePreferences from './pages/app/AppAutoSchedulePreferences'
import AppVoiceSettings from './pages/app/AppVoiceSettings'
import AppBrainSettings from './pages/app/AppBrainSettings'
import AppAdminPortal from './pages/app/AppAdminPortal'
import { VoiceAssistantProvider } from './contexts/VoiceAssistantContext'
import FloatingVoiceAssistant from './components/app/FloatingVoiceAssistant'

function App() {
    return (
        <AuthProvider>
            <AppProvider>
                <IntegrationsProvider>
                    <FocusProvider>
                        <Routes>
                            {/* Marketing Site */}
                            <Route path="/" element={
                                <div className="min-h-screen flex flex-col force-light">
                                    <Header />
                                    <main className="flex-1">
                                        <Home />
                                    </main>
                                    <Footer />
                                </div>
                            } />
                            <Route path="/how-it-works" element={
                                <div className="min-h-screen flex flex-col force-light">
                                    <Header />
                                    <main className="flex-1"><HowItWorks /></main>
                                    <Footer />
                                </div>
                            } />
                            <Route path="/features" element={
                                <div className="min-h-screen flex flex-col force-light">
                                    <Header />
                                    <main className="flex-1"><Features /></main>
                                    <Footer />
                                </div>
                            } />
                            <Route path="/pricing" element={
                                <div className="min-h-screen flex flex-col force-light">
                                    <Header />
                                    <main className="flex-1"><Pricing /></main>
                                    <Footer />
                                </div>
                            } />
                            <Route path="/about" element={
                                <div className="min-h-screen flex flex-col force-light">
                                    <Header />
                                    <main className="flex-1"><About /></main>
                                    <Footer />
                                </div>
                            } />
                            <Route path="/faq" element={
                                <div className="min-h-screen flex flex-col force-light">
                                    <Header />
                                    <main className="flex-1"><FAQ /></main>
                                    <Footer />
                                </div>
                            } />
                            <Route path="/blog" element={
                                <div className="min-h-screen flex flex-col force-light">
                                    <Header />
                                    <main className="flex-1"><Blog /></main>
                                    <Footer />
                                </div>
                            } />
                            <Route path="/signup" element={
                                <div className="min-h-screen flex flex-col force-light">
                                    <Header />
                                    <main className="flex-1"><Signup /></main>
                                    <Footer />
                                </div>
                            } />
                            <Route path="/login" element={
                                <div className="min-h-screen flex flex-col force-light">
                                    <Header />
                                    <main className="flex-1"><Login /></main>
                                    <Footer />
                                </div>
                            } />

                            {/* Protected App Routes - Mobile-first app interface */}
                            <Route path="/app" element={
                                <ProtectedRoute>
                                    <VoiceAssistantProvider>
                                        <AppLayout />
                                        <FloatingVoiceAssistant />
                                    </VoiceAssistantProvider>
                                </ProtectedRoute>
                            }>
                                <Route index element={<AppToday />} />
                                <Route path="calendar" element={<AppCalendar />} />
                                <Route path="inbox" element={<AppInbox />} />
                                <Route path="profile" element={<AppProfile />} />
                                <Route path="admin" element={<AppAdminPortal />} />
                                <Route path="connections" element={<AppConnections />} />
                                <Route path="changelog" element={<Changelog />} />
                            </Route>

                            {/* OAuth Callback Routes - NOT wrapped in ProtectedRoute 
                                because OAuth redirects cause full page reloads and auth 
                                state may not be ready yet. These pages handle their own 
                                redirects after processing tokens. */}
                            <Route path="/app/connections/callback" element={<AppOAuthCallback />} />
                            <Route path="/app/connections/slack/callback" element={<SlackOAuthCallback />} />
                            <Route path="/app/connections/notion/callback" element={<NotionOAuthCallback />} />

                            {/* Full-screen app modals (also protected) */}
                            <Route path="/app/focus" element={
                                <ProtectedRoute>
                                    <AppFocusMode />
                                </ProtectedRoute>
                            } />
                            <Route path="/app/voice" element={
                                <ProtectedRoute>
                                    <AppVoiceCapture />
                                </ProtectedRoute>
                            } />
                            <Route path="/app/analytics" element={
                                <ProtectedRoute>
                                    <AppAnalytics />
                                </ProtectedRoute>
                            } />
                            <Route path="/app/task/:id" element={
                                <ProtectedRoute>
                                    <AppTaskDetail />
                                </ProtectedRoute>
                            } />
                            <Route path="/app/event/:id" element={
                                <ProtectedRoute>
                                    <AppEventDetail />
                                </ProtectedRoute>
                            } />
                            <Route path="/app/schedule" element={
                                <ProtectedRoute>
                                    <AppScheduleNegotiation />
                                </ProtectedRoute>
                            } />
                            <Route path="/app/briefing" element={
                                <ProtectedRoute>
                                    <AppMorningBriefing />
                                </ProtectedRoute>
                            } />
                            <Route path="/app/debrief" element={
                                <ProtectedRoute>
                                    <VoiceAssistantProvider>
                                        <AppEveningDebrief />
                                        <FloatingVoiceAssistant />
                                    </VoiceAssistantProvider>
                                </ProtectedRoute>
                            } />
                            <Route path="/app/command-center" element={
                                <ProtectedRoute>
                                    <AppPriorityBoard />
                                </ProtectedRoute>
                            } />
                            <Route path="/app/habits" element={
                                <ProtectedRoute>
                                    <AppHabits />
                                </ProtectedRoute>
                            } />
                            <Route path="/app/goals" element={
                                <ProtectedRoute>
                                    <AppGoals />
                                </ProtectedRoute>
                            } />
                            <Route path="/app/help" element={
                                <ProtectedRoute>
                                    <AppHelpCenter />
                                </ProtectedRoute>
                            } />
                            <Route path="/app/contact" element={
                                <ProtectedRoute>
                                    <AppContactSupport />
                                </ProtectedRoute>
                            } />
                            <Route path="/app/features" element={
                                <ProtectedRoute>
                                    <AppFeatureRequests />
                                </ProtectedRoute>
                            } />
                            <Route path="/app/terms" element={
                                <ProtectedRoute>
                                    <AppTermsOfService />
                                </ProtectedRoute>
                            } />
                            <Route path="/app/privacy" element={
                                <ProtectedRoute>
                                    <AppPrivacyPolicy />
                                </ProtectedRoute>
                            } />
                            <Route path="/app/privacy-settings" element={
                                <ProtectedRoute>
                                    <AppPrivacySettings />
                                </ProtectedRoute>
                            } />
                            <Route path="/app/billing" element={
                                <ProtectedRoute>
                                    <AppSubscriptionBilling />
                                </ProtectedRoute>
                            } />
                            <Route path="/app/schedule-preferences" element={
                                <ProtectedRoute>
                                    <AppAutoSchedulePreferences />
                                </ProtectedRoute>
                            } />
                            <Route path="/app/voice-settings" element={
                                <ProtectedRoute>
                                    <AppVoiceSettings />
                                </ProtectedRoute>
                            } />
                            <Route path="/app/brain-settings" element={
                                <ProtectedRoute>
                                    <AppBrainSettings />
                                </ProtectedRoute>
                            } />
                        </Routes>
                    </FocusProvider>
                </IntegrationsProvider>
            </AppProvider>
        </AuthProvider>
    )
}

export default App
